import { useEffect, useMemo, useState, useCallback } from "react";
import { api } from "../lib/api";
import Layout from "../components/Layout";
import PageAdvice from "../components/PageAdvice";
import { CondReportCell, CondReportHeader } from "../components/CondReportCell";
import {
  CircleNotch, MagnifyingGlass, Cube, ArrowsClockwise, CheckCircle,
  CaretLeft, CaretRight, CaretUp, CaretDown, ArrowsDownUp,
  Funnel, Buildings, AddressBook, UsersThree, Tag, ArrowSquareOut,
} from "@phosphor-icons/react";

function SortHeader({ label, field, sort, onSort, align = "left", className = "" }) {
  const active = sort.by === field;
  return (
    <th className={`px-4 py-3 text-${align} ${className}`}>
      <button
        type="button"
        data-testid={`asset-sort-${field}`}
        onClick={() => onSort(field)}
        className={`inline-flex items-center gap-1 uppercase tracking-wider transition-colors hover:text-[#0055FF] ${active ? "text-[#0055FF]" : "text-neutral-500"}`}
      >
        {label}
        {active ? (
          sort.dir === "asc" ? <CaretUp size={12} weight="bold" /> : <CaretDown size={12} weight="bold" />
        ) : (
          <ArrowsDownUp size={12} className="text-neutral-300" />
        )}
      </button>
    </th>
  );
}

function FilterSelect({ icon: Icon, label, value, onChange, options, testid }) {
  return (
    <div className="min-w-[180px] flex-1">
      <label className="mb-1 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-widest text-neutral-500">
        <Icon size={13} weight="bold" /> {label}
      </label>
      <select data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-sm border border-neutral-300 bg-white px-3 py-2.5 text-sm outline-none focus:border-[#0055FF]">
        <option value="">All</option>
        {options.map((o) => (
          <option key={o.id} value={o.id}>{o.name}{o.count != null ? ` (${o.count})` : o.client_count != null ? ` (${o.client_count})` : ""}</option>
        ))}
      </select>
    </div>
  );
}

function fmtDate(s) {
  if (!s) return "—";
  return String(s).replace("T", " ").slice(0, 19);
}

export default function AssetList() {
  const [facets, setFacets] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [debounced, setDebounced] = useState("");
  const [filters, setFilters] = useState({ group_id: "", client_id: "", category_id: "", org_id: "" });
  const [sort, setSort] = useState({ by: "asset_name", dir: "asc" });

  const onSort = (field) => {
    setSort((p) => (p.by === field ? { by: field, dir: p.dir === "asc" ? "desc" : "asc" } : { by: field, dir: "asc" }));
    setPage(1);
  };

  useEffect(() => {
    api.get("/assets/facets").then((r) => setFacets(r.data)).catch(() => setFacets({ clients: [], categories: [], orgs: [], groups: [] }));
  }, []);

  useEffect(() => {
    const t = setTimeout(() => { setDebounced(search); setPage(1); }, 350);
    return () => clearTimeout(t);
  }, [search]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/assets/list", {
        params: { page, limit: 50, search: debounced, ...filters, sort_by: sort.by, sort_dir: sort.dir },
      });
      setData(data);
    } finally {
      setLoading(false);
    }
  }, [page, debounced, filters, sort]);

  useEffect(() => { load(); }, [load]);

  const sync = async () => {
    setSyncing(true);
    try {
      await api.post("/assets/sync");
      // Poll until the background sync finishes (can take ~60-90s for the full register).
      for (let i = 0; i < 120; i++) {
        await new Promise((r) => setTimeout(r, 2500));
        const { data } = await api.get("/assets/sync/status");
        if (!data.running) break;
      }
      const fr = await api.get("/assets/facets");
      setFacets(fr.data);
      setPage(1);
      await load();
    } finally { setSyncing(false); }
  };

  const setFilter = (k, v) => { setFilters((p) => ({ ...p, [k]: v })); setPage(1); };
  const clearFilters = () => { setFilters({ group_id: "", client_id: "", category_id: "", org_id: "" }); setSearch(""); setPage(1); };

  // Cascade: when a client group is chosen, the Client dropdown only shows that group's clients.
  const clientOptions = useMemo(() => {
    if (!facets) return [];
    if (!filters.group_id) return facets.clients;
    const grp = facets.groups.find((g) => g.id === filters.group_id);
    const ids = new Set(grp?.client_ids || []);
    return facets.clients.filter((c) => ids.has(c.id));
  }, [facets, filters.group_id]);

  // If the selected client is no longer valid under the chosen group, clear it.
  useEffect(() => {
    if (filters.client_id && !clientOptions.some((c) => c.id === filters.client_id)) {
      setFilters((p) => ({ ...p, client_id: "" }));
    }
  }, [clientOptions, filters.client_id]);

  const hasFilters = useMemo(() => search || Object.values(filters).some(Boolean), [search, filters]);
  const items = data?.items || [];

  return (
    <Layout wide>
      <div data-testid="asset-list-page">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <p className="text-sm uppercase tracking-widest font-semibold text-neutral-500">Assets</p>
            <h1 className="mt-1 font-heading text-4xl sm:text-5xl font-black tracking-tighter text-[#0A0A0A]">Asset List</h1>
            <p className="mt-2 max-w-3xl text-base text-neutral-600">
              The full asset register, synced from the Aroflo API into Asset Hub. Browse everything, or filter by
              client group, client, asset type or business unit.
            </p>
          </div>
          <div className="text-right">
            <button data-testid="assets-sync-button" onClick={sync} disabled={syncing}
              className="inline-flex items-center gap-2 rounded-sm bg-[#0055FF] px-4 py-2 text-sm font-semibold text-white transition-all duration-200 hover:bg-[#0044CC] disabled:opacity-60">
              {syncing ? <CircleNotch size={16} className="animate-spin" /> : <ArrowsClockwise size={16} weight="bold" />}
              {syncing ? "Syncing…" : "Sync from Aroflo"}
            </button>
            {data?.last_synced && (
              <p className="mt-1.5 flex items-center justify-end gap-1 text-xs text-neutral-500" data-testid="assets-last-synced">
                <CheckCircle size={12} weight="fill" className="text-[#059669]" /> Synced {fmtDate(data.last_synced)}
              </p>
            )}
            {data && data.complete != null && (
              data.complete ? (
                <p className="mt-1 flex items-center justify-end gap-1 text-xs font-semibold text-[#059669]" data-testid="assets-complete">
                  <CheckCircle size={12} weight="fill" /> Complete{data.expected_total != null ? ` · ${data.active_count}/${data.expected_total} in DB` : ""}
                </p>
              ) : (
                <p className="mt-1 flex items-center justify-end gap-1 text-xs font-semibold text-[#D97706]" data-testid="assets-incomplete">
                  <ArrowsClockwise size={12} weight="bold" /> Syncing incomplete{data.expected_total != null ? ` · ${data.active_count}/${data.expected_total}` : ""} — click Sync
                </p>
              )
            )}
          </div>
        </div>

        {/* Filters */}
        <div className="mt-6 rounded-sm border border-neutral-200 bg-white p-4 shadow-sm">
          <div className="flex flex-wrap items-end gap-4">
            {facets && (
              <>
                <FilterSelect testid="filter-group" icon={UsersThree} label="Client group" value={filters.group_id} onChange={(v) => setFilter("group_id", v)} options={facets.groups} />
                <FilterSelect testid="filter-client" icon={AddressBook} label={filters.group_id ? "Client (in group)" : "Client"} value={filters.client_id} onChange={(v) => setFilter("client_id", v)} options={clientOptions} />
                <FilterSelect testid="filter-category" icon={Tag} label="Asset type" value={filters.category_id} onChange={(v) => setFilter("category_id", v)} options={facets.categories} />
                <FilterSelect testid="filter-org" icon={Buildings} label="Business unit" value={filters.org_id} onChange={(v) => setFilter("org_id", v)} options={facets.orgs} />
              </>
            )}
          </div>
          <div className="mt-4 flex flex-wrap items-center gap-3">
            <div className="relative flex-1 min-w-[240px]">
              <MagnifyingGlass size={18} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-neutral-400" />
              <input data-testid="asset-search" value={search} onChange={(e) => setSearch(e.target.value)}
                placeholder="Search name, barcode, serial, model, manufacturer, client…"
                className="w-full rounded-sm border border-neutral-300 bg-white py-2.5 pl-11 pr-4 text-base outline-none focus:border-[#0055FF]" />
            </div>
            {hasFilters && (
              <button data-testid="clear-filters" onClick={clearFilters}
                className="inline-flex items-center gap-1.5 rounded-sm border border-neutral-300 bg-white px-3 py-2 text-sm font-semibold text-neutral-700 hover:bg-neutral-50">
                <Funnel size={14} weight="bold" /> Clear
              </button>
            )}
          </div>
        </div>

        <p className="mt-3 text-sm text-neutral-500" data-testid="asset-count">
          {loading ? "Loading…" : `${(data?.count ?? 0).toLocaleString()} assets${hasFilters ? " match" : ""}`}
        </p>

        {/* Table */}
        <div className="mt-2 overflow-x-auto border border-neutral-200 bg-white shadow-sm rounded-sm">
          {loading ? (
            <div className="flex h-64 items-center justify-center"><CircleNotch size={28} className="animate-spin text-[#0055FF]" /></div>
          ) : items.length === 0 ? (
            <div className="flex h-64 flex-col items-center justify-center gap-2" data-testid="asset-empty">
              <Cube size={28} className="text-neutral-300" />
              <p className="text-sm text-neutral-500">No assets match your filters.</p>
            </div>
          ) : (
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="border-b border-neutral-200 bg-neutral-50 text-xs uppercase tracking-wider text-neutral-500">
                  <SortHeader label="Asset name" field="asset_name" sort={sort} onSort={onSort} />
                  <SortHeader label="Asset type" field="category_name" sort={sort} onSort={onSort} />
                  <SortHeader label="Client" field="client_name" sort={sort} onSort={onSort} />
                  <SortHeader label="Business unit" field="org_name" sort={sort} onSort={onSort} />
                  <SortHeader label="Barcode" field="barcode" sort={sort} onSort={onSort} />
                  <SortHeader label="Serial" field="serial_number" sort={sort} onSort={onSort} />
                  <SortHeader label={<CondReportHeader />} field="cond_report" sort={sort} onSort={onSort} align="center" />
                  <th className="px-4 py-3 text-right" />
                </tr>
              </thead>
              <tbody>
                {items.map((a) => (
                  <tr key={a.asset_id} data-testid={`asset-row-${a.asset_id}`}
                    onClick={() => window.open(`/asset-detail?id=${encodeURIComponent(a.asset_id)}`, "_blank", "noopener")}
                    className="cursor-pointer border-b border-neutral-100 even:bg-neutral-50/60 hover:bg-[#0055FF]/[0.03]">
                    <td className="max-w-[280px] truncate px-4 py-3 font-semibold text-[#0A0A0A]" title={a.asset_name}>{a.asset_name || "—"}</td>
                    <td className="max-w-[180px] truncate px-4 py-3 text-neutral-700" title={a.category_name}>{a.category_name || "—"}</td>
                    <td className="max-w-[200px] truncate px-4 py-3 text-neutral-700" title={a.client_name}>{a.client_name || "—"}</td>
                    <td className="max-w-[180px] truncate px-4 py-3 text-neutral-700" title={a.org_name}>{a.org_name || "—"}</td>
                    <td className="px-4 py-3 font-mono text-xs text-neutral-500">{a.barcode || "—"}</td>
                    <td className="px-4 py-3 font-mono text-xs text-neutral-500">{a.serial_number || "—"}</td>
                    <td className="px-4 py-3 text-center"><CondReportCell hasReport={a.has_condition_report} hasValues={a.has_condition_values} testid={`asset-cond-${a.asset_id}`} /></td>
                    <td className="px-4 py-3 text-right"><ArrowSquareOut size={15} className="ml-auto text-neutral-300" /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Pagination */}
        {data && data.pages > 1 && (
          <div className="mt-4 flex items-center justify-between" data-testid="asset-pagination">
            <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)} data-testid="asset-prev"
              className="inline-flex items-center gap-1 rounded-sm border border-neutral-300 bg-white px-3 py-2 text-sm font-semibold text-neutral-700 hover:bg-neutral-50 disabled:opacity-40">
              <CaretLeft size={14} weight="bold" /> Prev
            </button>
            <span className="text-sm text-neutral-500">Page {data.page} of {data.pages}</span>
            <button disabled={page >= data.pages} onClick={() => setPage((p) => p + 1)} data-testid="asset-next"
              className="inline-flex items-center gap-1 rounded-sm border border-neutral-300 bg-white px-3 py-2 text-sm font-semibold text-neutral-700 hover:bg-neutral-50 disabled:opacity-40">
              Next <CaretRight size={14} weight="bold" />
            </button>
          </div>
        )}

        <PageAdvice items={[
          "Assets are pulled from the Aroflo API into Asset Hub's own database — the table loads instantly and stays available even if the API is slow.",
          "Use ‘Sync from Aroflo’ to refresh; new assets are added, changed ones updated, and assets that disappear are flagged (never deleted).",
          "‘Client group’ filters by the clients in your internal groups (matched by client name across API versions). Click any row for the full asset record incl. custom fields.",
        ]} />
      </div>
    </Layout>
  );
}
